// WebXR (VR / AR) types + session init constants. Pure data — reducer
// in state-machine.ts; detection in detection.ts; orchestrator wiring
// (modal open/close, adapter calls, ESC chain) in orchestrator.ts;
// adapter session lifecycle (`enterXR`/`exitXR`) in mkkellogg-adapter.ts.

export type XRMode = 'vr' | 'ar';

export interface XRSupport {
  hasWebXR: boolean;
  vrSupported: boolean;
  arSupported: boolean;
}

export type XRStatus = 'idle' | 'detecting' | 'modal-open' | 'starting' | 'in-session' | 'error';

export interface XRState {
  status: XRStatus;
  modalOpen: boolean;
  support: XRSupport;
  activeMode: XRMode | null;
  lastError: string | null;
}

export type XREvent =
  | { type: 'OPEN_MODAL' }
  | { type: 'CLOSE_MODAL' }
  | { type: 'DETECT_RESULT'; support: XRSupport }
  | { type: 'SESSION_REQUEST'; mode: XRMode }
  | { type: 'SESSION_STARTED'; mode: XRMode }
  | { type: 'SESSION_ENDED' }
  | { type: 'ERROR'; message: string };

export interface XRSnapshot {
  status: XRStatus;
  modalOpen: boolean;
  support: XRSupport;
  activeMode: XRMode | null;
  lastError: string | null;
}

// MVP session features. AR requires `local-floor` so the splat scene
// (anchored at Y=0) lines up with the user's room. NO `dom-overlay`
// for 4.K (would require `domOverlay.root` we don't pass — UA may
// reject requestSession; defer to 4.K2 with overlay root + cleanup).
export const VR_SESSION_INIT: XRSessionInit = {
  optionalFeatures: ['local-floor'],
};
export const AR_SESSION_INIT: XRSessionInit = {
  requiredFeatures: ['local-floor'],
};

export const INITIAL_XR_SUPPORT: XRSupport = Object.freeze({
  hasWebXR: false,
  vrSupported: false,
  arSupported: false,
});

export const INITIAL_XR_STATE: XRState = Object.freeze({
  status: 'idle',
  modalOpen: false,
  support: INITIAL_XR_SUPPORT,
  activeMode: null,
  lastError: null,
}) as XRState;
